/**
 * The statistics page's figures, build round 37 (2026-10-11; decided by Mattej via Portal HQ on
 * 2026-10-10, Öppet O-024). Pure, so every state is tested without a browser.
 *
 * The rules, read exactly as the round wrote them:
 * - A month with a row in `private.site_count_months` is measured, and a kind without a row is 0. A
 *   month without that row is "Mäts inte", never 0. `public.client_site_counts` answers `measured`.
 * - A kind with an unreviewed plausibility flag is "Kontrolleras": the gateway sends no figure, and its
 *   comparisons are hidden. A reviewed flag shows the figure. A figure is never edited.
 * - A comparison shows only when both months are measured; otherwise that comparison alone reads
 *   "Mäts inte".
 * - Every figure is compared with the month before and with the same month last year, as a number and
 *   a plain difference. No colour and no word says good or bad: a drop in clicks is not red.
 * - The month shown is the last full calendar month in Europe/Stockholm, and the picker offers the last
 *   twelve. The 25 months kept are exactly those twelve and their year-ago months.
 */

export type CountKind = "form" | "tel" | "mailto" | "buy";

/** The order the page shows results in: the form first, as the nearest thing to a real enquiry. */
export const KIND_ORDER: CountKind[] = ["form", "tel", "mailto", "buy"];

/** The glossary key of each kind (app/lib/statisticsGlossary.json). */
export const KIND_GLOSSARY: Record<CountKind, "form" | "calls" | "mails" | "buys"> = {
  form: "form",
  tel: "calls",
  mailto: "mails",
  buy: "buys"
};

/** One row of `public.client_site_counts`. */
export interface ClientSiteCount {
  service_id: string;
  display_name: string;
  public_url: string | null;
  month: string;
  kind: CountKind;
  measured: boolean;
  checking: boolean;
  total: number | null;
}

export type Cell =
  | { state: "measured"; value: number }
  | { state: "not_measured" }
  | { state: "checking" };

export interface SiteCounts {
  serviceId: string;
  /** The address the section is named by: the host of the public URL, or the service's name. */
  address: string;
  kinds: CountKind[];
  /** kind -> month (`YYYY-MM`) -> cell. A month absent from the map is not measured. */
  cells: Map<CountKind, Map<string, Cell>>;
}

const stockholm = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Stockholm", year: "numeric", month: "2-digit" });

/** The Stockholm month a moment falls in, as `YYYY-MM`. */
export function stockholmMonth(now: Date): string {
  const parts = Object.fromEntries(stockholm.formatToParts(now).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}`;
}

/** The month `back` months before a `YYYY-MM` month. */
export function monthsBack(month: string, back: number): string {
  const [year, number] = month.split("-").map(Number);
  const index = year * 12 + (number - 1) - back;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;
}

/** The last full Stockholm month: the month before the one now runs in. */
export function lastFullMonth(now: Date = new Date()): string {
  return monthsBack(stockholmMonth(now), 1);
}

/** The twelve months the picker offers, newest first. Nothing older: the year-ago months need the rest. */
export function pickerMonths(now: Date = new Date()): string[] {
  const last = lastFullMonth(now);
  return Array.from({ length: 12 }, (_, back) => monthsBack(last, back));
}

const monthLong = new Intl.DateTimeFormat("sv-SE", { month: "long", year: "numeric", timeZone: "UTC" });
const monthShort = new Intl.DateTimeFormat("sv-SE", { month: "short", timeZone: "UTC" });

/** "september 2026". */
export function monthWords(month: string): string {
  const [year, number] = month.split("-").map(Number);
  return monthLong.format(new Date(Date.UTC(year, number - 1, 1)));
}

/** "sep", three letters as the overview's bars write them. */
export function monthShortWord(month: string): string {
  const [year, number] = month.split("-").map(Number);
  return monthShort.format(new Date(Date.UTC(year, number - 1, 1))).replace(/\.$/, "").slice(0, 3);
}

/** The host of a public URL, or the service's name when there is none. */
export function siteAddress(publicUrl: string | null, displayName: string): string {
  if (publicUrl) {
    try {
      return new URL(publicUrl).host.replace(/^www\./, "");
    } catch {
      // A stored URL that does not parse is named by the service instead.
    }
  }
  return displayName;
}

/** The gateway's rows as one section per website, in the order the gateway gives them. */
export function siteCounts(rows: ClientSiteCount[]): SiteCounts[] {
  const sites = new Map<string, SiteCounts>();
  for (const row of rows) {
    let site = sites.get(row.service_id);
    if (!site) {
      site = { serviceId: row.service_id, address: siteAddress(row.public_url, row.display_name), kinds: [], cells: new Map() };
      sites.set(row.service_id, site);
    }
    if (!site.cells.has(row.kind)) {
      site.cells.set(row.kind, new Map());
    }
    const month = row.month.slice(0, 7);
    const cell: Cell = !row.measured
      ? { state: "not_measured" }
      : row.checking || row.total === null
        ? { state: "checking" }
        : { state: "measured", value: row.total };
    site.cells.get(row.kind)!.set(month, cell);
  }
  for (const site of sites.values()) {
    site.kinds = KIND_ORDER.filter((kind) => site.cells.has(kind));
  }
  return [...sites.values()];
}

/** A site's cell for a kind and month; a month the gateway did not answer is not measured. */
export function cellOf(site: SiteCounts, kind: CountKind, month: string): Cell {
  return site.cells.get(kind)?.get(month) ?? { state: "not_measured" };
}

export type Comparison =
  | { state: "shown"; difference: number; percent: number | null }
  | { state: "not_measured" }
  | { state: "hidden" };

/**
 * A figure against another month's. Hidden while the figure itself is held for checking; "Mäts inte"
 * when either month is not measured, or the other month is held; otherwise the plain difference and,
 * when the other month is not 0, the percentage.
 */
export function compare(current: Cell, other: Cell): Comparison {
  if (current.state === "checking") {
    return { state: "hidden" };
  }
  if (current.state !== "measured" || other.state !== "measured") {
    return { state: "not_measured" };
  }
  const difference = current.value - other.value;
  const percent = other.value === 0 ? null : Math.round((difference / other.value) * 100);
  return { state: "shown", difference, percent };
}

const whole = new Intl.NumberFormat("sv-SE");
const MINUS = "−";

/** "+4", "−12" or "0", with a real minus sign. */
export function signed(value: number): string {
  if (value === 0) {
    return "0";
  }
  return `${value > 0 ? "+" : MINUS}${whole.format(Math.abs(value))}`;
}

/** "+4 (+12 %)", "−3 (−50 %)", "+2" when the other month was 0, "0 (0 %)". */
export function comparisonWords(comparison: Comparison): string {
  if (comparison.state === "hidden") {
    return "";
  }
  if (comparison.state === "not_measured") {
    return "Mäts inte";
  }
  const percent = comparison.percent === null ? "" : ` (${signed(comparison.percent)} %)`;
  return `${signed(comparison.difference)}${percent}`;
}

/** A count in Swedish figures. */
export function countWords(value: number): string {
  return whole.format(value);
}

/* --- Visits, from public.analytics_monthly_visits --------------------------------------------------- */

export interface MonthVisits {
  analytics_connection_id: string;
  month: string;
  visits: number;
  mobile_share: number | null;
  speed_rating: string | null;
  sources: Array<{ host: string | null; visits: number }> | null;
}

/** A month's stored visits figures for one connection, or null when the month was not stored. */
export function visitsOf(rows: MonthVisits[], connectionId: string | null, month: string): MonthVisits | null {
  if (!connectionId) {
    return null;
  }
  return rows.find((row) => row.analytics_connection_id === connectionId && row.month.slice(0, 7) === month) ?? null;
}

/** A number as a cell: a stored month is measured, a missing one is not. */
export function numberCell(value: number | null | undefined): Cell {
  return value === null || value === undefined ? { state: "not_measured" } : { state: "measured", value };
}

/** A difference in percentage points for the share on a phone: "+3 procentenheter". */
export function pointsWords(comparison: Comparison): string {
  if (comparison.state !== "shown") {
    return comparisonWords(comparison);
  }
  return `${signed(comparison.difference)} procentenheter`;
}

/* --- Google Search, from public.client_search_months (build round 38, 2026-10-11) ------------------ */

/** One row of `public.client_search_months`: a stored month is measured; a month without a row is not. */
export interface SearchMonth {
  service_id: string;
  month: string;
  clicks: number;
  impressions: number;
  position: number | null;
  top_queries: Array<{ key: string; clicks: number }>;
}

/** A service's stored Search Console month, or null when it was not read. */
export function searchOf(rows: SearchMonth[], serviceId: string, month: string): SearchMonth | null {
  return rows.find((row) => row.service_id === serviceId && row.month.slice(0, 7) === month) ?? null;
}

const oneDecimal = new Intl.NumberFormat("sv-SE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Google's average position with one decimal: "6,8". */
export function positionNumber(value: number): string {
  return oneDecimal.format(value);
}

/**
 * Average position compared as a plain difference with one decimal and no percentage, decided by
 * Mattej via Portal HQ on 2026-10-11: "−1,2" when it moved up the page, "+0,4" when down, "0,0".
 */
export function positionWords(comparison: Comparison): string {
  if (comparison.state !== "shown") {
    return comparisonWords(comparison);
  }
  const rounded = Math.round(comparison.difference * 10) / 10;
  if (rounded === 0) {
    return oneDecimal.format(0);
  }
  return `${rounded > 0 ? "+" : MINUS}${oneDecimal.format(Math.abs(rounded))}`;
}

/* --- Google Business Profile, read live (build round 47, 2026-10-27) ------------------------------- */

/**
 * One month of the live Business Profile read. Never stored (decision 0614). A figure is null when
 * Google had no day for that month, which reads "Mäts inte" and never 0.
 */
export interface ProfileMonth {
  month: string;
  calls: number | null;
  website_clicks: number | null;
  directions: number | null;
  views: number | null;
}

export type ProfileFigure = "views" | "calls" | "website_clicks" | "directions";

/** The order the page shows the four in, each with its glossary key. */
export const PROFILE_FIGURES: Array<{ figure: ProfileFigure; glossaryKey: string }> = [
  { figure: "views", glossaryKey: "profile_views" },
  { figure: "calls", glossaryKey: "profile_calls" },
  { figure: "website_clicks", glossaryKey: "profile_website_clicks" },
  { figure: "directions", glossaryKey: "profile_directions" }
];

/** A month's figure as a cell; a month the read did not answer, or answered null, is not measured. */
export function profileCell(rows: ProfileMonth[], figure: ProfileFigure, month: string): Cell {
  return numberCell(rows.find((row) => row.month === month)?.[figure] ?? null);
}
